/// WorkerManager: typed worker lifecycle manager wrapping WorkerHost.
/// Provides typed command sending, event subscription, and worker lifecycle
/// management with a singleton pattern for the React application.

import { WorkerHost, type EventListener } from "@lib/WorkerHost.js";
import type { MessageEnvelope } from "@lib/MessageEnvelope.js";
import type {
  EngineCommand,
  EngineEventType,
  EditorCommand,
  EditorEventType,
} from "@/protocol";

// ---------------------------------------------------------------------------
// Worker role definitions
// ---------------------------------------------------------------------------

/** Supported worker roles in the application. */
export type WorkerRole = "bpmn-engine" | "node-engine" | "bpmn-editor" | "node-editor";

/** Configuration for registering a worker. */
interface WorkerRegistration {
  workerId: string;
  role: WorkerRole;
  worker: Worker;
}

// ---------------------------------------------------------------------------
// WorkerManager
// ---------------------------------------------------------------------------

/**
 * Typed worker lifecycle manager wrapping WorkerHost.
 *
 * Responsibilities:
 * - Register and unregister WASM workers by ID and role
 * - Send typed commands (engine or editor) and receive typed responses
 * - Subscribe to typed events from workers
 * - Provide singleton access for the React application
 */
class WorkerManager {
  private host: WorkerHost;
  private roles = new Map<string, WorkerRole>();
  /** Multiple workers can share the same role (e.g. two node-editor tabs). */
  private workersByRole = new Map<WorkerRole, Set<string>>();

  constructor() {
    this.host = new WorkerHost();
  }

  // -------------------------------------------------------------------------
  // Worker lifecycle
  // -------------------------------------------------------------------------

  /** Register a worker with the manager. */
  registerWorker(registration: WorkerRegistration): void {
    const { workerId, role, worker } = registration;
    this.host.register(workerId, worker);
    this.roles.set(workerId, role);
    if (!this.workersByRole.has(role)) {
      this.workersByRole.set(role, new Set());
    }
    this.workersByRole.get(role)?.add(workerId);
  }

  /** Unregister and terminate a worker. */
  unregisterWorker(workerId: string): void {
    const role = this.roles.get(workerId);
    if (role) {
      const roleSet = this.workersByRole.get(role);
      if (roleSet) {
        roleSet.delete(workerId);
        if (roleSet.size === 0) {
          this.workersByRole.delete(role);
        }
      }
    }
    this.roles.delete(workerId);
    this.host.unregister(workerId);
  }

  /** Get the worker ID for a given role (returns the most recently added), or undefined. */
  getWorkerIdByRole(role: WorkerRole): string | undefined {
    const roleSet = this.workersByRole.get(role);
    if (!roleSet || roleSet.size === 0) return undefined;
    // Return the last inserted ID (iteration order of Set is insertion order)
    let last: string | undefined;
    for (const id of roleSet) {
      last = id;
    }
    return last;
  }

  /** Get all worker IDs registered for a given role. */
  getWorkerIdsByRole(role: WorkerRole): string[] {
    const roleSet = this.workersByRole.get(role);
    return roleSet ? [...roleSet] : [];
  }

  /** Get the role for a given worker ID. */
  getWorkerRole(workerId: string): WorkerRole | undefined {
    return this.roles.get(workerId);
  }

  /** Whether at least one worker with the given role is registered. */
  hasRole(role: WorkerRole): boolean {
    const roleSet = this.workersByRole.get(role);
    return roleSet !== undefined && roleSet.size > 0;
  }

  // -------------------------------------------------------------------------
  // Command sending
  // -------------------------------------------------------------------------

  /**
   * Send a typed engine command to a worker and wait for a response.
   * @param workerId - The target worker ID
   * @param command - The typed engine command
   * @param timeoutMs - Request timeout in milliseconds (default 10000)
   * @returns The response MessageEnvelope
   */
  async sendCommand(
    workerId: string,
    command: EngineCommand | EditorCommand,
    timeoutMs = 10000,
  ): Promise<MessageEnvelope> {
    return this.host.sendRequest(
      workerId,
      command as unknown as Omit<MessageEnvelope, "correlation_id">,
      timeoutMs,
    );
  }

  /**
   * Send a command to a worker identified by role.
   * Throws if no worker is registered with the given role.
   */
  async sendCommandToRole(
    role: WorkerRole,
    command: EngineCommand | EditorCommand,
    timeoutMs = 10000,
  ): Promise<MessageEnvelope> {
    const workerId = this.getWorkerIdByRole(role);
    if (!workerId) {
      throw new Error(`WorkerManager: no worker registered for role "${role}"`);
    }
    return this.sendCommand(workerId, command, timeoutMs);
  }

  // -------------------------------------------------------------------------
  // Event subscription
  // -------------------------------------------------------------------------

  /**
   * Subscribe to a specific engine event type from any worker.
   * @returns An unsubscribe function.
   */
  onEngineEvent(eventType: EngineEventType, listener: EventListener): () => void {
    return this.host.addEventListener(eventType, listener);
  }

  /**
   * Subscribe to a specific editor event type from any worker.
   * @returns An unsubscribe function.
   */
  onEditorEvent(eventType: EditorEventType, listener: EventListener): () => void {
    return this.host.addEventListener(eventType, listener);
  }

  /**
   * Subscribe to all events from any worker.
   * @returns An unsubscribe function.
   */
  onEvent(listener: EventListener): () => void {
    return this.host.addGeneralListener(listener);
  }

  // -------------------------------------------------------------------------
  // Underlying host access (for advanced usage)
  // -------------------------------------------------------------------------

  /** Get the underlying WorkerHost instance. */
  getHost(): WorkerHost {
    return this.host;
  }

  /** Number of registered workers. */
  get workerCount(): number {
    return this.host.size;
  }
}

// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------

let _instance: WorkerManager | null = null;

/** Get or create the singleton WorkerManager instance. */
export function getWorkerManager(): WorkerManager {
  if (!_instance) {
    _instance = new WorkerManager();
  }
  return _instance;
}

